import { useQuery } from '@tanstack/react-query'
import { get } from '../../lib/api.js'
import { keys } from '../../lib/queryKeys.js'

// Every request the landing screen makes, in one file, so the screen holds no
// URLs (§11a).
//
// Today is a summary of screens that already exist, so every figure on it
// comes from the report that figure already belongs to — the day book for
// what was sold and bought, the ageing report for what customers owe,
// stock-on-hand for what is on the floor, and /api/sales for the bills
// themselves. One endpoint here — outstanding-payable — did not exist before
// this screen needed it: the Suppliers list already prints what is owed to
// each supplier, one row at a time, but nothing anywhere summed it across all
// of them. That gap got a report of its own rather than a client-side SUM()
// over a paginated list, which would have been wrong the moment a shop had
// more suppliers than one page holds. Everything else stays a read of a
// report that already existed, so a "dashboard numbers" endpoint duplicating
// figures the shop can read elsewhere never gets the chance to disagree with
// them.
//
// Nothing in this file computes anything. Money arrives as a string, the count
// beside it as an integer, and both are forwarded untouched (Rule 2).
//
// Keys come from lib/queryKeys.js, deliberately from the same factories the
// reports and invoices screens use: `invalidates.postSale` already names
// ['reports','day-book'], ['sales'] and ['outstanding'], so posting a bill at
// the counter refreshes this screen on the way back to it, and there is no
// second invalidation map to keep in step with the first.

const PATHS = {
  dayBook: '/api/reports/day-book',
  stock: '/api/reports/stock-on-hand',
  ageing: '/api/reports/outstanding-ageing',
  payable: '/api/reports/outstanding-payable',
  sales: '/api/sales',
  purchases: '/api/purchases',
  customers: '/api/customers',
  suppliers: '/api/suppliers',
}

/**
 * `?a=1&b=2`, skipping anything absent.
 *
 * Copied per feature rather than shared — it is four lines, and reports/ and
 * invoices/ each carry their own for the same reason. Nothing this screen sends
 * is repeatable, so unlike those two this copy has no array branch.
 */
function toQuery(params) {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    search.set(key, String(value))
  }
  const query = search.toString()
  return query ? `?${query}` : ''
}

// ── what each request asks for ───────────────────────────────────────────────

/**
 * One row, because only the summary is read.
 *
 * The endpoint's minimum is 1 and its default is 50 — and the day book returns
 * four registers, so the default would ship up to two hundred document rows
 * this screen never renders. The summaries come back either way; they are
 * separate aggregates over the same predicate, not a fold of the rows.
 */
const SUMMARY_ONLY = { limit: 1 }

/**
 * Stock wants one thing more than its summary: the single most-stocked product.
 *
 * That row is how the set-up checklist knows whether stock has been entered at
 * all. `low_stock_products` cannot answer it — a shop that has entered nothing
 * has nothing below its reorder level either — and there is no count endpoint
 * for "products with stock". Sorted by current_stock descending, the first row
 * is the maximum, so one row settles it for the whole master.
 */
const STOCK_FILTERS = { limit: 1, sort: 'current_stock:desc' }

/**
 * The Stock Alerts strip's own four cards: the products actually at or
 * below their reorder level, worst first. `low_stock: true` rides the same
 * partial index (idx_products_low_stock) the summary's own count is
 * filtered by, so the four cards and the "N low stock" badge above them can
 * never disagree about which predicate "low" means.
 */
const STOCK_ALERT_FILTERS = { limit: 4, sort: 'current_stock:asc', low_stock: true }

/** How many rows Recent Sales and Recent Purchases each show before handing
 *  over to /invoices or /purchases. Each list's own `total` is what says how
 *  many there really are. */
export const RECENT_SHOWN = 8

/**
 * Newest first.
 *
 * Every row in this table is the same date, so `invoice_date:desc` — the
 * Invoices screen's default — would leave them in whatever order the tiebreak
 * fell in. A number is allocated late in the posting transaction, so descending
 * invoice number is the order the bills were actually written in, and the one
 * somebody just posted is the top row.
 */
const SALES_SORT = 'invoice_number:desc'

/**
 * Newest first, for purchases — but by date, not by number: unlike an
 * invoice number, `purchase_number` has no sortable index (§07a lists only
 * the keys it can serve off one), so the closest available approximation is
 * the document date, descending. Ties on the same day fall back to id, which
 * is not meaningful order but is at least a stable one across pages.
 */
const PURCHASE_SORT = 'purchase_date:desc'

/**
 * The set-up checklist's two counts change about twice in a shop's life, and
 * they are counts of a row this screen does not render. Held for five minutes
 * rather than the client's thirty seconds: nothing is decided from them except
 * whether a tick is filled in.
 */
const CHECKLIST_STALE_MS = 5 * 60 * 1000

// ── the tiles ────────────────────────────────────────────────────────────────

/**
 * The day's registers, and with them the shop's own today.
 *
 * Sent with no dates on purpose: the default day comes from the database, in
 * IST, and the response echoes it back as `date_from`/`date_to`. A browser that
 * worked out "today" at 23:40 on 31 March would ask for 1 April and disagree
 * with the invoice numbers drawn that evening — so this one answer is the only
 * date the rest of the screen is allowed to be built from.
 *
 * `scope` says whose entries these are: the whole shop for a caller who holds
 * `purchases`, their own for everybody else. The screen prints which.
 */
export function useDayBook(enabled, dates = null) {
  // `dates` is null for Today, and that is not the same as "today's dates".
  // Sending nothing lets the database answer at the moment the query runs; see
  // range.js for the evening on which the difference decides the answer.
  const filters = dates ? { ...SUMMARY_ONLY, ...dates } : SUMMARY_ONLY
  return useQuery({
    queryKey: keys.dayBook(filters),
    queryFn: ({ signal }) => get(`${PATHS.dayBook}${toQuery(filters)}`, { signal }),
    enabled,
  })
}

/**
 * Receivables, for the total and the over-90 figure under it.
 *
 * DELIBERATELY NOT FILTERED BY THE DASHBOARD'S RANGE, and the same goes for
 * stock below. What a shop is owed is a position, not a sum over a period: it
 * is true as of now and has no meaning "for last month". Passing the range in
 * here would answer a different question — bills *raised* in that window, still
 * unpaid — which is a real report but not this tile, and a manager reading
 * "Total outstanding" after picking last month would work the collections list
 * from a number that leaves out everything older.
 *
 * The screen says "as of today" on these two whenever the range is not today,
 * so the distinction is on the page rather than only in this comment.
 */
export function useAgeing(enabled) {
  return useQuery({
    queryKey: keys.outstanding(SUMMARY_ONLY),
    queryFn: ({ signal }) => get(`${PATHS.ageing}${toQuery(SUMMARY_ONLY)}`, { signal }),
    enabled,
  })
}

/**
 * What the shop owes its suppliers, right now — the payable twin of
 * useAgeing above, and DELIBERATELY NOT FILTERED BY THE RANGE either, for
 * the same reason: it is a position, not a sum over a period.
 *
 * No `limit`, no `SUMMARY_ONLY`: unlike every other request on this page,
 * outstanding-payable has no rows to page through — it is one summary
 * object, full stop — so there is no pagination contract to opt out of here.
 */
export function useOutstandingPayable(enabled) {
  return useQuery({
    queryKey: keys.outstandingPayable(),
    queryFn: ({ signal }) => get(PATHS.payable, { signal }),
    enabled,
  })
}

/** What is on the floor: how much is at or below its reorder level, what the
 *  whole of it is worth (view_cost only — the server strips that field, not
 *  this screen), and the one row the checklist reads. */
export function useStock(enabled) {
  return useQuery({
    queryKey: keys.stockOnHand(STOCK_FILTERS),
    queryFn: ({ signal }) => get(`${PATHS.stock}${toQuery(STOCK_FILTERS)}`, { signal }),
    enabled,
  })
}

/**
 * The Stock Alerts cards themselves — a second, differently-filtered read of
 * the same report `useStock` above already calls. Two requests rather than
 * asking for `low_stock=true` once and deriving the "one most-stocked
 * product" checklist question from it: that question needs the single
 * highest holding in the WHOLE master, which is never among the low-stock
 * rows this one returns.
 *
 * DELIBERATELY NOT FILTERED BY THE DASHBOARD'S RANGE, same as stock value
 * and the two outstanding tiles — what is low on the shelf right now has no
 * meaning "for last month".
 */
export function useStockAlerts(enabled) {
  return useQuery({
    queryKey: keys.stockOnHand(STOCK_ALERT_FILTERS),
    queryFn: ({ signal }) => get(`${PATHS.stock}${toQuery(STOCK_ALERT_FILTERS)}`, { signal }),
    enabled,
  })
}

// ── recent sales and purchases ──────────────────────────────────────────────

/**
 * The bills posted in the range on screen — Recent Sales.
 *
 * `day` comes from the day book's echoed date and nothing else, which is why
 * this request waits for that one: `enabled` is false until the server has said
 * what day it is. The alternative — asking the browser — is the 31 March bug
 * above, on the screen every session lands on.
 *
 * Unlike the tiles, this is the whole shop's list for every caller who may
 * sell, exactly as /invoices is. The tiles above it can be one cashier's; these
 * rows are not, and the screen says so rather than quietly mixing the two.
 */
export function useRecentSales(day, enabled, dates = null) {
  // For Today the window is the single day the server named; for any other
  // preset it is the range the picker resolved — which was itself derived from
  // that same server day, so the browser clock never enters into it.
  const window = dates ?? { date_from: day, date_to: day }
  const filters = { ...window, limit: RECENT_SHOWN, sort: SALES_SORT }
  return useQuery({
    queryKey: keys.sales(filters),
    queryFn: ({ signal }) => get(`${PATHS.sales}${toQuery(filters)}`, { signal }),
    // Still gated on the server's day even when an explicit range is supplied:
    // the range came from the anchor, so before the anchor exists there is
    // nothing to ask for.
    enabled: Boolean(day) && enabled,
  })
}

/**
 * The purchases recorded in the same range — Recent Purchases, the buying
 * side's own twin of the query above. Always the whole shop's list: unlike
 * /api/sales, /api/purchases has no per-cashier scope to begin with, since it
 * is reachable only with `purchases`, which this screen already treats as a
 * whole-shop capability everywhere else on it.
 */
export function useRecentPurchases(day, enabled, dates = null) {
  const window = dates ?? { date_from: day, date_to: day }
  const filters = { ...window, limit: RECENT_SHOWN, sort: PURCHASE_SORT }
  return useQuery({
    queryKey: keys.purchases(filters),
    queryFn: ({ signal }) => get(`${PATHS.purchases}${toQuery(filters)}`, { signal }),
    enabled: Boolean(day) && enabled,
  })
}

// ── the set-up checklist ─────────────────────────────────────────────────────
//
// Two counts, and both are usually free. See the note beside their callers: the
// ageing report has already proved that bills and customers exist whenever it
// returns a single row, and these run only when it has not.

/** Has this shop ever posted a bill. `total` is the §07a list contract's count
 *  over the same predicate, taken from a request for one row. */
export function useSaleCount(enabled) {
  return useQuery({
    queryKey: keys.sales(SUMMARY_ONLY),
    queryFn: ({ signal }) => get(`${PATHS.sales}${toQuery(SUMMARY_ONLY)}`, { signal }),
    enabled,
    staleTime: CHECKLIST_STALE_MS,
  })
}

/** Has this shop added a customer. */
export function useCustomerCount(enabled) {
  return useQuery({
    queryKey: keys.customers(SUMMARY_ONLY),
    queryFn: ({ signal }) => get(`${PATHS.customers}${toQuery(SUMMARY_ONLY)}`, { signal }),
    enabled,
    staleTime: CHECKLIST_STALE_MS,
  })
}

// ── the overview tiles ───────────────────────────────────────────────────────

/**
 * How many suppliers this shop has on file — the §07a list contract's own
 * count, from a one-row request. There is no cheaper way to prove it: unlike
 * customers, nothing else this screen asks for (day book, ageing, stock)
 * establishes that a supplier exists, so the Suppliers tile always sends this
 * one rather than inferring it from a request made for another reason.
 */
export function useSupplierTotal(enabled) {
  return useQuery({
    queryKey: keys.suppliers(SUMMARY_ONLY),
    queryFn: ({ signal }) => get(`${PATHS.suppliers}${toQuery(SUMMARY_ONLY)}`, { signal }),
    enabled,
  })
}
